import { FormattedEncounter } from "../../../encounters/types";
import { RecommendedTask } from "../../../encounters/components/recommended-task";
import { PatientDetailsConditional } from "~/features/dashboard/components/patient/patient-details-conditional";
import { NotesContainer } from "~/features/dashboard/components/notes";
import { getRouteApi } from "@tanstack/react-router";
import { ConsultationStatus } from "../consultation-status";
import { Info, CircleCheck, Clock } from "lucide-react";
import { Button } from "@workspace/web-ui/components/button";
import { Image } from "@workspace/web-ui/components/image";
import { useSendActionNeededMutation } from "../../../patient-logs/api/mutations";
import { toast } from "sonner";

const COLORS = {
    TEXT_PRIMARY: "#2D2F35",
    TEXT_SECONDARY: "#667085",
} as const;

interface ConsultationDetailsProps {
    encounter: FormattedEncounter
}

export const TodoConsultationDetails = ({ encounter }: ConsultationDetailsProps) => {
    const routeApi = getRouteApi("/(dashboard)/dashboard/$program");
    const { program } = routeApi.useParams();
    const { mutate: sendActionNeeded, isPending: sendActionpending } = useSendActionNeededMutation();

    // Check for medication_payment tasks to determine resolved/action needed status
    const normalizedTasks = Array.isArray(encounter.recommendedTasks) ? encounter.recommendedTasks : [];
    const medicationPaymentTasks = normalizedTasks.filter(
        (task: any) => task?.type === "medication_payment"
    );
    
    // There may be multiple medication_payment tasks.
    const anyCompletedPayment = medicationPaymentTasks.some(
        (task: any) => task?.status === "completed"
    );
    const anyNotCompletedPayment = medicationPaymentTasks.some(
        (task: any) => task?.status !== "completed"
    );
    
    // Find the first completed task for erx check
    const completedPaymentTask = medicationPaymentTasks.find(
        (task: any) => task?.status === "completed"
    );
    
    // Only show status tags if the consultation has happened or is happening
    const hasConsultationOccurred = ["happening", "past", "completed", "attended"].includes(encounter.status);
    
    // showResolvedTag only if ALL are completed and is_erx_sent is true for the completed one.
    // showActionNeededTag if ANY are incomplete, OR if all are completed but is_erx_sent is NOT true for the completed one.
    const showResolvedTag = hasConsultationOccurred && completedPaymentTask?.is_erx_sent === true && !anyNotCompletedPayment;
    const showActionNeededTag = hasConsultationOccurred && (
        (anyNotCompletedPayment && anyCompletedPayment) || // some completed, some not
        (anyCompletedPayment && !anyNotCompletedPayment && completedPaymentTask?.is_erx_sent !== true) // all completed, but is_erx_sent is NOT true
    );
    const showInProgressTag =
        hasConsultationOccurred && !anyCompletedPayment && anyNotCompletedPayment;

    return (
        <>
            {program === "weight-loss" && (
                <div className="flex flex-col w-full h-full bg-[#FFFFFF] rounded-[24px] overflow-auto p-4 sm:py-[16px]">
                    {/* Patient info section */}
                    <div className="flex items-center gap-2 mb-4">
                        <div className="flex gap-2 items-center">
                            <ConsultationStatus
                                tag="Consultation"
                                program={program}
                            />
                            {showResolvedTag && (
                                <span className="text-xs font-medium py-[2px] px-[6px] rounded-full text-[#16A34A] bg-[#ECFDF3] border border-[#BAE6C3] w-fit flex items-center gap-1">
                                    <CircleCheck className="size-[14px] text-[#16A34A]" />
                                    Resolved
                                </span>
                            )}
                            {showActionNeededTag && (
                                <span className="text-xs font-medium py-[2px] px-[6px] rounded-full text-[#FFA726] bg-[#FFF5E6] border w-fit flex items-center gap-1">
                                    <Info className="size-[14px] text-[#FFA726]" />
                                    Action needed
                                </span>
                            )}
                            {showInProgressTag && (
                                <span className="text-xs font-medium py-[2px] px-[6px] rounded-full text-[#2563EB] bg-[#EFF6FF] border w-fit flex items-center gap-1">
                                    <Clock className="size-[14px] text-[#2563EB]" />
                                    In progress
                                </span>
                            )}
                        </div>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-3 sm:gap-2 items-start sm:items-center justify-between mb-1 w-full">
                        <div className="flex flex-col gap-2 w-full sm:w-auto">
                            <div className="flex gap-2 items-center">
                                <h2 className="text-2xl sm:text-[32px] text-[#2D2F35] mb-1 font-medium max-w-full">
                                    {encounter?.title}
                                </h2>
                                {encounter.patient.medical_plan?.payment_status === "paid" && encounter.patient.glp1_guide_accepted && encounter.patient.identity_verified && encounter.patient.supplementary_health_assessment && Object.keys(encounter.patient.supplementary_health_assessment).length > 0 && (
                                    <div className="">
                                        <Image src="/icons/check.svg" alt="timeline" layout="constrained" className="w-4 h-4 sm:w-5 sm:h-5" width={96} height={96} />
                                    </div>
                                )}
                            </div>

                            <div className="flex flex-wrap items-center gap-y-1 gap-x-2">
                                {encounter.patient.age && (
                                    <>
                                        <span className="text-sm" style={{ color: COLORS.TEXT_PRIMARY }}>
                                            {encounter?.patient.age} years
                                        </span>
                                    </>
                                )}
                                {encounter?.patient.gender && (
                                    <>
                                        <span className="hidden sm:inline mx-1" style={{ color: COLORS.TEXT_SECONDARY }}>
                                            •
                                        </span>
                                        <span className="text-sm" style={{ color: COLORS.TEXT_PRIMARY }}>
                                            {encounter?.patient.gender}
                                        </span>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-col w-full text-left pb-3 mb-6">
                        <h2 className="text-[#2D2F35] font-medium text-base mb-4">Request Information</h2>
                        <div className="grid grid-cols-[150px_1fr] gap-8">
                            <div className="flex flex-col gap-2">
                                <p className="text-[#464B55] text-sm">Program:</p>
                                <p className="text-[#464B55] text-sm">Request Date:</p>
                                <p className="text-[#1A202C] text-sm">Consultation:</p>
                            </div>
                            <div className="flex flex-col gap-2">
                                <p className="text-[#1A202C] text-sm">Weight Loss</p>
                                <p className="text-[#1A202C] text-sm">{encounter?.createdAt}</p>
                                <p className="text-[#1A202C] text-sm">Express consultation</p>
                            </div>
                        </div>
                    </div>

                    <PatientDetailsConditional encounter={encounter} program={program} />

                    {/* Notes Section */}
                    {encounter?.patient?.medical_plan?.payment_status === "paid" &&
                        encounter?.patient?.glp1_guide_accepted &&
                        encounter?.patient?.identity_verified &&
                        encounter?.patient?.supplementary_health_assessment &&
                        Object.keys(encounter.patient.supplementary_health_assessment).length > 0 && (
                            <div className="mt-4">
                                <NotesContainer
                                    encounterId={encounter.encounterId}
                                    patientId={encounter.patient.id}
                                />
                            </div>
                        )
                    }

                    {/* Recommended Tasks */}
                    {!(encounter?.patient?.medical_plan?.payment_status === "paid" &&
                        encounter?.patient?.glp1_guide_accepted &&
                        encounter?.patient?.identity_verified &&
                        encounter?.patient?.supplementary_health_assessment &&
                        Object.keys(encounter.patient.supplementary_health_assessment).length > 0) && (
                        <div className="flex items-center mt-4 bg-[#F0F5FF] rounded-lg px-4 py-2 justify-between w-full">
                            <p className="text-sm text-[#1A202C]">
                                Patient must be verified
                            </p>
                            <Button
                                className="button-primary !bg-[#4272DD] h-[44px] !text-white flex gap-2 items-center !shadow-none w-fit !border-none"
                                onClick={async () => {
                                    try {
                                        await sendActionNeeded({ patient_id: encounter.patient.id });
                                        toast.success("Reminder sent to patient to complete verification.");
                                    } catch (error) {
                                        toast.error("Failed to send reminder.");
                                    }
                                }}
                                disabled={sendActionpending}
                            >
                                {sendActionpending && (
                                    <svg className="animate-spin h-4 w-4 mr-2 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                                    </svg>
                                )}
                                Send a Reminder
                            </Button>
                        </div>
                    )}
                    {encounter.status !== "happening" && (
                        <RecommendedTask 
                            encounterId={encounter.encounterId} 
                            patientId={encounter.patient.id} 
                            showAddButton={
                                encounter?.patient?.medical_plan?.payment_status === "paid" &&
                                encounter?.patient?.glp1_guide_accepted &&
                                encounter?.patient?.identity_verified &&
                                encounter?.patient?.supplementary_health_assessment &&
                                Object.keys(encounter.patient.supplementary_health_assessment).length > 0
                            }
                        />
                    )}
                </div>
            )}
            {program === "urgent-care" && (
                <div className="flex flex-col w-full h-full bg-[#FFFFFF] rounded-[24px] overflow-auto p-4 sm:py-[16px]">
                    <div className="flex items-center gap-2 mb-4">
                        <ConsultationStatus
                            tag={
                                Array.isArray(encounter.recommendedTasks) &&
                                encounter.recommendedTasks.some(
                                    (task) => task.type === "medication_payment"
                                )
                                    ? "Resolved"
                                    : "Consultation"
                            }
                            program={program}
                        />
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3 sm:gap-2 items-start sm:items-center justify-between mb-1 w-full">
                        <div className="flex flex-col gap-2 w-full sm:w-auto">
                            <h2 className="text-2xl sm:text-[32px] text-[#2D2F35] mb-1 font-medium max-w-full flex flex-row gap-2 sm:gap-3 sm:items-center items-start">
                                {encounter?.title}

                                {encounter.patient.urgent_care_onboarded && encounter.patient.identity_verified && (
                                    <div className="flex-shrink-0 mt-1 sm:mt-0">
                                        <Image src="/icons/check.svg"
                                            alt="timeline"
                                            className="w-4 h-4 sm:w-5 sm:h-5" layout="constrained" width={96} height={96} />
                                    </div>
                                )}
                            </h2>
                        </div>
                    </div>

                    <div className="flex flex-col w-full text-left pb-3 mb-4 mt-4">
                        <div className="flex justify-between w-[80%]">
                            <div className="text-[#464B55] text-sm">
                                Submitted:
                                <p className="text-[#1A202C] text-sm">{encounter?.createdAt}</p>
                            </div>
                            <div className="text-[#464B55] text-sm">
                                Program:
                                <p className="text-[#1A202C] text-sm">Urgent Care</p>
                            </div>
                        </div>
                    </div>

                    <PatientDetailsConditional encounter={encounter} program={program} />

                    {encounter.patient.urgent_care_onboarded && (
                        <NotesContainer
                            encounterId={encounter.encounterId}
                            patientId={encounter.patient.id}
                            notes={encounter.notes}
                        />
                    )}
                </div>
            )}
        </>
    );
};

